import type { ICellRendererParams } from "ag-grid-community";
import moment from "moment";
import { useMemo, type ReactNode } from "react";

import { formatAuditSource, formatAuditUser } from "./auditDisplayUtils";
import {
  CATEGORY_BADGE_VARIANT,
  CATEGORY_LABEL,
  SEVERITY_DOT_CLASSES,
  getTaxonomy,
} from "./eventTaxonomy";
import { AuditTableRow, isAuditEventTableRow } from "./auditTableUtils";

import { Badge, BadgeSize } from "@/components/ui/Badge";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { TableColumn } from "@/components/ui/TableV2";
import { cn } from "@/lib/utils";

const LEFT_CELL_CLASS = "justify-start text-left";
const LEFT_HEADER_CLASS = "!justify-start !text-left";

const AuditCell = ({ children }: { children: ReactNode }) => (
  <div className="flex h-full w-full min-w-0 items-center justify-start px-3 text-left">
    {children}
  </div>
);

const TruncatedAuditText = ({ content, className }: { content: string; className?: string }) => {
  if (content === "—") {
    return <span className={className}>{content}</span>;
  }

  return (
    <OverflowDisplayTooltip content={content} className="min-w-0 flex-1">
      <span className={cn("block truncate", className)}>{content}</span>
    </OverflowDisplayTooltip>
  );
};

export const useAuditLogColumns = () => {
  const columns = useMemo<TableColumn<AuditTableRow>[]>(
    () => [
      {
        id: 1,
        name: "event",
        field: "event_type",
        header: "Event",
        sortable: false,
        minWidth: 240,
        flex: 1.6,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          const taxonomy = getTaxonomy(row.event_type);
          return (
            <AuditCell>
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT_CLASSES[taxonomy.severity]}`}
                  aria-hidden="true"
                />
                <TruncatedAuditText
                  content={taxonomy.label}
                  className="text-sm font-medium text-gray-900"
                />
              </div>
            </AuditCell>
          );
        },
      },
      {
        id: 2,
        name: "category",
        field: "event_type",
        header: "Category",
        sortable: false,
        minWidth: 120,
        maxWidth: 140,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          const taxonomy = getTaxonomy(row.event_type);
          return (
            <AuditCell>
              <Badge
                variant={CATEGORY_BADGE_VARIANT[taxonomy.category]}
                size={BadgeSize.XS}
                text={CATEGORY_LABEL[taxonomy.category]}
                className="uppercase tracking-wide"
              />
            </AuditCell>
          );
        },
      },
      {
        id: 3,
        name: "actor",
        field: "actor_email",
        header: "Actor",
        sortable: false,
        minWidth: 160,
        flex: 1,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          return (
            <AuditCell>
              <TruncatedAuditText
                content={formatAuditUser(row.actor_email, row.actor_user_id)}
                className="text-sm text-gray-700"
              />
            </AuditCell>
          );
        },
      },
      {
        id: 4,
        name: "target",
        field: "target_email",
        header: "Target",
        sortable: false,
        minWidth: 160,
        flex: 1,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          return (
            <AuditCell>
              <TruncatedAuditText
                content={formatAuditUser(row.target_email, row.target_user_id)}
                className="text-sm text-gray-700"
              />
            </AuditCell>
          );
        },
      },
      {
        id: 5,
        name: "source",
        field: "source",
        header: "Source",
        sortable: false,
        minWidth: 120,
        maxWidth: 150,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          return (
            <AuditCell>
              <TruncatedAuditText
                content={formatAuditSource(row.source)}
                className="text-sm text-gray-500"
              />
            </AuditCell>
          );
        },
      },
      {
        id: 6,
        name: "time",
        field: "created_at",
        header: "Time",
        sortable: false,
        minWidth: 88,
        maxWidth: 96,
        cellClass: LEFT_CELL_CLASS,
        headerClass: LEFT_HEADER_CLASS,
        cellRenderer: (params: ICellRendererParams<AuditTableRow>) => {
          const row = params.data;
          if (!row || !isAuditEventTableRow(row)) {
            return null;
          }
          return (
            <AuditCell>
              <span
                className="font-mono text-xs text-gray-500"
                title={moment(row.created_at).format("MMM D, YYYY h:mm:ss A")}
              >
                {moment(row.created_at).format("h:mm A")}
              </span>
            </AuditCell>
          );
        },
      },
    ],
    [],
  );

  return { columns };
};
